import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Modal, Button, Row, Col, Form } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { ThemeContext } from '../contexts/ThemeContext';
import { getTradingStatistics } from '../services/api';
import { 
  Chart, 
  BarElement, 
  BarController,
  LinearScale, 
  CategoryScale, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';

// Register Chart.js components
Chart.register(
  BarElement, 
  BarController,
  LinearScale, 
  CategoryScale, 
  Tooltip, 
  Legend,
  Filler
);

const EdgeHistoryModal = ({ show, onHide, accountId, accountName, timePeriod, onTimePeriodChange, accounts, onAccountChange }) => {
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [periodsToShow, setPeriodsToShow] = useState(12);
  // Always follow app theme
  const { effectiveTheme: chartTheme } = useContext(ThemeContext);

  const loadHistoricalEdgeData = useCallback(async () => {
    setLoading(true);
    try {
      const data = [];
      const now = new Date();
      
      for (let i = periodsToShow - 1; i >= 0; i--) {
        let startDate, endDate, periodLabel;
        
        switch (timePeriod) {
          case '1week':
            // Calculate week periods (Monday to Sunday)
            const weekStart = new Date(now);
            weekStart.setDate(now.getDate() - (now.getDay() + 6) % 7 - (i * 7));
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            
            startDate = weekStart;
            endDate = weekEnd;
            periodLabel = `Week ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            break;
            
          case '1month':
            // Calculate month periods (1st to last day of month)
            const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
            
            startDate = monthStart;
            endDate = monthEnd;
            periodLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            break;
            
          case '3months':
            // Calculate 3-month periods
            const quarterStart = new Date(now.getFullYear(), now.getMonth() - (i * 3), 1);
            const quarterEnd = new Date(now.getFullYear(), now.getMonth() - (i * 3) + 3, 0);
            
            startDate = quarterStart;
            endDate = quarterEnd;
            periodLabel = `${quarterStart.toLocaleDateString('en-US', { month: 'short' })} - ${quarterEnd.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
            break;
            
          case '6months':
            // Calculate 6-month periods
            const halfYearStart = new Date(now.getFullYear(), now.getMonth() - (i * 6), 1);
            const halfYearEnd = new Date(now.getFullYear(), now.getMonth() - (i * 6) + 6, 0);
            
            startDate = halfYearStart;
            endDate = halfYearEnd;
            periodLabel = `${halfYearStart.toLocaleDateString('en-US', { month: 'short' })} - ${halfYearEnd.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
            break;
            
          case '1year':
            // Calculate year periods
            const yearStart = new Date(now.getFullYear() - i, 0, 1);
            const yearEnd = new Date(now.getFullYear() - i, 11, 31);
            
            startDate = yearStart;
            endDate = yearEnd;
            periodLabel = yearStart.getFullYear().toString();
            break;
            
          default:
            // Default to 1 month
            const defaultMonthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const defaultMonthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
            
            startDate = defaultMonthStart;
            endDate = defaultMonthEnd;
            periodLabel = defaultMonthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        }

        // Fetch trading statistics for this period
                  try {
            const params = {
              start_date: startDate.toISOString().split('T')[0],
              end_date: endDate.toISOString().split('T')[0]
            };
          
          // Only add account_id if a specific account is selected
          if (accountId && accountId !== 'all') {
            params.account_id = accountId;
          }
          
          console.log(`Fetching edge data for ${periodLabel}:`, params);
          
          const response = await getTradingStatistics(params);
          console.log(`Raw response for ${periodLabel}:`, response);
          const stats = response.data;
          console.log(`Stats data for ${periodLabel}:`, stats);
          console.log(`Expectancy (weighted):`, stats.expectancy_position_weighted, ` | Raw:`, stats.expectancy);
          console.log(`Closed trades:`, stats.closed_trades);
          
          data.push({
            period: periodLabel,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            edge: (typeof stats.expectancy_position_weighted === 'number') ? stats.expectancy_position_weighted : 0,
            trades: stats.closed_trades || 0,
            winRate: stats.closed_trades > 0 ? ((stats.winning_trades || 0) / stats.closed_trades * 100) : 0
          });
        } catch (error) {
          console.error(`Error fetching data for period ${periodLabel}:`, error);
          data.push({
            period: periodLabel,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            edge: 0,
            trades: 0,
            winRate: 0
          });
        }
      }
      
      setHistoricalData(data);
    } catch (error) {
      console.error('Error loading historical edge data:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId, timePeriod, periodsToShow]);

  useEffect(() => {
    if (show && accountId) {
      loadHistoricalEdgeData();
    }
  }, [show, accountId, timePeriod, periodsToShow, loadHistoricalEdgeData]);

  // Re-render chart when theme changes
  useEffect(() => {
    if (show) {
      setHistoricalData(h => h.length > 0 ? [...h] : h);
    }
  }, [chartTheme, show]);

  const chartData = {
    labels: historicalData.map(d => d.period),
    datasets: [
      {
        label: 'Position‑weighted Edge (%)',
        data: historicalData.map(d => d.edge),
        backgroundColor: historicalData.map(d => d.edge >= 0 ? 
          (chartTheme === 'light' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(34, 197, 94, 0.9)') : 
          (chartTheme === 'light' ? 'rgba(239, 68, 68, 0.8)' : 'rgba(239, 68, 68, 0.9)')
        ),
        borderColor: historicalData.map(d => d.edge >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'),
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
        barThickness: 'flex',
        maxBarThickness: 50
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: chartTheme === 'light' ? '#ffffff' : '#1f2937',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: chartTheme === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(33, 37, 41, 0.95)',
        titleColor: chartTheme === 'light' ? '#212529' : '#f8f9fa',
        bodyColor: chartTheme === 'light' ? '#212529' : '#f8f9fa',
        borderColor: chartTheme === 'light' ? '#dee2e6' : '#495057',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const dataIndex = context.dataIndex;
            const data = historicalData[dataIndex];
            return [
              `Edge: ${data.edge.toFixed(2)}%`,
              `Trades: ${data.trades}`,
              `Win Rate: ${data.winRate.toFixed(1)}%`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: chartTheme === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          callback: function(value) {
            return value + '%';
          },
          color: chartTheme === 'light' ? '#212529' : '#f8f9fa'
        }
      },
      x: {
        grid: {
          color: chartTheme === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          maxRotation: 30,
          minRotation: 0,
          color: chartTheme === 'light' ? '#212529' : '#f8f9fa'
        }
      }
    }
  };

  const getTimePeriodLabel = () => {
    switch (timePeriod) {
      case '1week': return 'Weekly';
      case '1month': return 'Monthly';
      case '3months': return 'Quarterly';
      case '6months': return '6-Month';
      case '1year': return 'Yearly';
      default: return 'Monthly';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header 
        closeButton
        style={{ 
          backgroundColor: chartTheme === 'light' ? '#f8f9fa' : '#374151',
          borderBottomColor: chartTheme === 'light' ? '#dee2e6' : '#4b5563'
        }}
      >
        <Modal.Title style={{ 
          color: chartTheme === 'light' ? '#212529' : '#f8f9fa'
        }}>
          {accountId && accountId !== 'all' ? (
            <>
              Account: {accountName || 'Loading...'} - 
              Historical Trading Edge - {getTimePeriodLabel()} Data
            </>
          ) : accountId === 'all' ? (
            <>
              All Accounts - 
              Historical Trading Edge - {getTimePeriodLabel()} Data
            </>
          ) : (
            `Historical Trading Edge - ${getTimePeriodLabel()} Data`
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ 
        backgroundColor: chartTheme === 'light' ? '#ffffff' : '#1f2937',
        color: chartTheme === 'light' ? '#212529' : '#f8f9fa'
      }}>
        {!accountId ? (
          <div className="text-center py-5">
            <i className="bi bi-info-circle text-muted" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 text-muted">Account Selection Required</h5>
            <p className="text-muted">
              Please select an account from the dropdown above to view historical edge data.
            </p>
          </div>
        ) : (
                      <>
                            <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={{ color: chartTheme === 'light' ? '#212529' : '#f8f9fa' }}>Account</Form.Label>
                    <Form.Select 
                      value={accountId || 'all'} 
                      onChange={(e) => onAccountChange(e.target.value)}
                      style={{
                        backgroundColor: chartTheme === 'light' ? '#ffffff' : '#374151',
                        color: chartTheme === 'light' ? '#212529' : '#f8f9fa',
                        borderColor: chartTheme === 'light' ? '#dee2e6' : '#4b5563'
                      }}
                    >
                      <option value="all">All Accounts</option>
                      {accounts && accounts.map((account) => (
                        <option key={account.account_id} value={account.account_id}>
                          {account.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={{ color: chartTheme === 'light' ? '#212529' : '#f8f9fa' }}>Time Period</Form.Label>
                    <Form.Select 
                      value={timePeriod} 
                      onChange={(e) => onTimePeriodChange(e.target.value)}
                      style={{
                        backgroundColor: chartTheme === 'light' ? '#ffffff' : '#374151',
                        color: chartTheme === 'light' ? '#212529' : '#f8f9fa',
                        borderColor: chartTheme === 'light' ? '#dee2e6' : '#4b5563'
                      }}
                    >
                      <option value="1week">1 Week</option>
                      <option value="1month">1 Month</option>
                      <option value="3months">3 Months</option>
                      <option value="6months">6 Months</option>
                      <option value="1year">1 Year</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={{ color: chartTheme === 'light' ? '#212529' : '#f8f9fa' }}>Periods to Show</Form.Label>
                    <Form.Select 
                      value={periodsToShow} 
                      onChange={(e) => setPeriodsToShow(parseInt(e.target.value))}
                      style={{
                        backgroundColor: chartTheme === 'light' ? '#ffffff' : '#374151',
                        color: chartTheme === 'light' ? '#212529' : '#f8f9fa',
                        borderColor: chartTheme === 'light' ? '#dee2e6' : '#4b5563'
                      }}
                    >
                      <option value={4}>4 Periods</option>
                      <option value={6}>6 Periods</option>
                      <option value={8}>8 Periods</option>
                      <option value={12} selected>12 Periods</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {/* Chart theme follows app theme; no manual override */}
              </Row>
            
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading historical data...</p>
              </div>
            ) : (
              <div 
                style={{ 
                  height: '300px',
                  backgroundColor: chartTheme === 'light' ? '#ffffff' : '#1f2937',
                  borderRadius: '8px',
                  padding: '16px'
                }}
              >
                <Bar data={chartData} options={chartOptions} />
              </div>
            )}
            
            <div className="mt-3">
              <small className="text-muted">
                <i className="bi bi-info-circle me-1"></i>
                Chart shows position‑weighted expectancy (CLOSED trades per period). Hover for details.
              </small>

            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ 
        backgroundColor: chartTheme === 'light' ? '#f8f9fa' : '#374151',
        borderTopColor: chartTheme === 'light' ? '#dee2e6' : '#4b5563'
      }}>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EdgeHistoryModal;
